import {
  translate,
  translatedPropertyFromKey,
  LANGUAGES,
  DEFAULT_LANGUAGE,
} from "./locales";
import { Intro, IntroPage } from "./components/intro_page";
import { RecommendationPage } from "./components/recommendation_page";
import { OptinPage } from "./components/optin_page";
import { BoostPage } from "./components/boost_page";
import { EndPage, EndContent } from "./components/end_page";
import { Rating } from "./components/recommendation/rating";
import { BoostSelection } from "./components/boost/boost_selection";
import { RadioInput } from "./components/optin/radio_input";
import { NumberInput } from "./components/optin/number_input";

const params = new URLSearchParams(window.location.search);
const bpact_id = params.get("linkname") === "bp" ? params.get("p_id") : null;

const forEachLocale = (value) =>
  Object.fromEntries(LANGUAGES.map((locale) => [locale, value]));

export const recommendationKeys = Object.freeze(
  Object.keys(translate("recommendations", {}, DEFAULT_LANGUAGE))
    .filter((key) => key !== "page")
    .sort(() => Math.random() - 0.5),
);

export const ratingKey = (key) => `${key}-rating`;

export function getSurveySettings(locale) {
  let collectorURL = import.meta.env.VITE_COLLECTOR_URL;
  if (collectorURL === undefined && import.meta.env.DEV) {
    collectorURL = "http://localhost:3300";
  }

  let order = 0;
  const nextOrder = () => order++;

  return {
    key: "my-survey",
    responseCollector: {
      apiSettings: {
        url: collectorURL,
        projectKey: import.meta.env.VITE_COLLECTOR_KEY,
      },
      defaultValues: {
        language: locale,
        surveyVersion: "v1",
        initialAnswers: {
          bpact_id,
        },
      },
    },
    pages: [
      createIntroPage(nextOrder),
      ...createRecommendationPages(nextOrder, recommendationKeys),
      createOptinPage(nextOrder),
      createBoostPage(nextOrder, recommendationKeys),
      createEndPage(nextOrder),
    ],
  };
}

function createIntroPage(nextOrder) {
  return {
    key: "intro",
    order: nextOrder(),
    title: translatedPropertyFromKey("intro.title"),
    sections: [
      {
        key: "intro",
        type: "content",
        order: 1,
        component: Intro,
      },
    ],
    component: IntroPage,
  };
}

function createRecommendationPages(nextOrder, recommendations) {
  const scaleOptions = [1, 2, 3, 4, 5].map((n) => ({
    value: n,
    label: translatedPropertyFromKey(`recommendations.page.scale.${n}`),
  }));

  return recommendations.map((key, index) => ({
    key,
    order: nextOrder(),
    title: translatedPropertyFromKey(`recommendations.${key}.title`),
    sections: [
      {
        key: ratingKey(key),
        type: "radio",
        order: 1,
        options: scaleOptions,
        component: Rating,
      },
      {
        key: "completed",
        type: "hidden",
        order: 2,
        defaultValue: index === recommendations.length - 1,
      },
    ],
    component: RecommendationPage,
  }));
}

function createOptinPage(nextOrder) {
  return {
    key: "optin",
    order: nextOrder(),
    title: translatedPropertyFromKey("optin.title"),
    sections: [
      {
        key: "optin-bank",
        type: "radio",
        order: 1,
        required: true,
        title: translatedPropertyFromKey("optin.bank.label"),
        options: [
          { value: "yes", label: translatedPropertyFromKey("optin.bank.yes") },
          { value: "no", label: translatedPropertyFromKey("optin.bank.no") },
        ],
        component: RadioInput,
      },
      {
        key: "optin-birth-year",
        type: "number",
        order: 2,
        title: translatedPropertyFromKey("optin.birth_year.label"),
        placeholder: forEachLocale("9999"),
        min: 1000,
        max: 9999,
        component: NumberInput,
      },
      {
        key: "optin-gender",
        type: "radio",
        order: 3,
        required: true,
        title: translatedPropertyFromKey("optin.gender.label"),
        options: [
          {
            value: "vrouw",
            label: translatedPropertyFromKey("optin.gender.vrouw"),
          },
          {
            value: "man",
            label: translatedPropertyFromKey("optin.gender.man"),
          },
          { value: "x", label: translatedPropertyFromKey("optin.gender.x") },
        ],
        component: RadioInput,
      },
      {
        key: "optin-postcode",
        type: "number",
        order: 4,
        title: translatedPropertyFromKey("optin.postcode.label"),
        placeholder: forEachLocale("9999"),
        min: 1000,
        max: 9999,
        component: NumberInput,
      },
      {
        /* for the honeypot */
        key: "optin-education",
        type: "radio",
        order: 5,
        title: translatedPropertyFromKey("optin.education.label"),
        options: [
          {
            value: "primary",
            label: translatedPropertyFromKey("optin.education.primary"),
          },
          {
            value: "secondary",
            label: translatedPropertyFromKey("optin.education.secondary"),
          },
          {
            value: "higher",
            label: translatedPropertyFromKey("optin.education.higher"),
          },
        ],
        component: RadioInput,
      },
    ],
    component: OptinPage,
  };
}

function createBoostPage(nextOrder, recommendations) {
  return {
    key: "boost",
    order: nextOrder(),
    title: translatedPropertyFromKey("boost.title"),
    sections: [
      {
        key: "boost-priorities",
        type: "checkboxes",
        order: 1,
        dependsOn: recommendations.map(ratingKey),
        options: recommendations.map((recKey) => ({
          value: recKey,
          label: translatedPropertyFromKey(
            `recommendations.${recKey}.short_title`,
          ),
          hideIf: (treesponse) => {
            const rating = treesponse.getAnswer(ratingKey(recKey));
            return rating === null || rating < 4;
          },
        })),
        component: BoostSelection,
      },
    ],
    component: BoostPage,
  };
}

function createEndPage(nextOrder) {
  return {
    key: "end",
    order: nextOrder(),
    title: translatedPropertyFromKey("end.title"),
    sections: [
      {
        key: "end-content",
        type: "content",
        order: 1,
        component: EndContent,
      },
    ],
    component: EndPage,
  };
}
